/**
 * WebSocket服务模块
 * 提供全局WebSocket连接实例和相关功能
 */

import { moveCharacterToPosition } from '../truman/characterMovement.js';

// WebSocket连接实例
let ws = null;

// 连接状态
let connectionState = 'disconnected'; // 'disconnected', 'connecting', 'connected'

// 消息处理器映射
const messageHandlers = new Map();

// 重连设置
const MAX_RETRY_COUNT = 5;
const RETRY_DELAY = 2000; // 毫秒
let retryCount = 0;
let retryTimeout = null;

// 请求防抖设置
const DEBOUNCE_DELAY = 500; // 毫秒
const recentRequests = new Map(); // 用于存储最近的请求


/**
 * 初始化WebSocket连接
 * @param {string} token - 认证令牌
 * @returns {Promise} 连接成功的Promise
 */
export function initWebSocketConnection() {
    // 如果已经连接或正在连接，则返回
    if (connectionState === 'connected') {
        return Promise.resolve(ws);
    }

    if (connectionState === 'connecting') {
        // 如果正在连接中，返回一个Promise，等待连接完成或超时
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('WebSocket connection timeout'));
            }, 5000); // 5秒超时

            const checkConnection = setInterval(() => {
                if (connectionState === 'connected') {
                    clearTimeout(timeout);
                    clearInterval(checkConnection);
                    resolve(ws);
                } else if (connectionState === 'disconnected') {
                    clearTimeout(timeout);
                    clearInterval(checkConnection);
                    reject(new Error('WebSocket connection failed'));
                }
            }, 100);
        });
    }

    connectionState = 'connecting';

    return new Promise((resolve, reject) => {
        try {
            // 使用绝对路径，直接连接到服务器
            const TRUMAN_API_KEY = import.meta.env.VITE_TRUMAN_API_KEY;
            const SERVER_URL = import.meta.env.VITE_WEBSOCKET_SERVER_URL;

            // 根据当前环境使用相应的WebSocket协议
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const wsUrl = `${protocol}//${SERVER_URL}/ws?token=${TRUMAN_API_KEY}`;

            console.log('Connecting to WebSocket server:', {
                serverUrl: SERVER_URL,
                protocol: protocol,
                fullUrl: wsUrl,
                currentLocationProtocol: window.location.protocol
            });

            // 创建WebSocket连接
            ws = new WebSocket(wsUrl);

            // 设置WebSocket事件处理器
            ws.onopen = function(event) {
                console.log('WebSocket connected');
                // showNotification('已连接到游戏服务器', 'success');
                connectionState = 'connected';
                retryCount = 0; // 重置重试计数
                resolve(ws);
            };

            ws.onclose = function(event) {
                console.log('WebSocket closed');

                // 如果不是主动关闭，则尝试重新连接
                if (connectionState !== 'disconnected') {
                    connectionState = 'disconnected';
                    reject(new Error('WebSocket connection closed'));
                    attemptReconnect();
                }
            };

            ws.onerror = function(error) {
                console.error('WebSocket错误:', error);

                if (connectionState === 'connecting') {
                    connectionState = 'disconnected';
                    reject(error);
                    attemptReconnect();
                }
            };

            ws.onmessage = function(event) {
                handleWebSocketMessage(event);
            };
        } catch (error) {
            console.error('初始化WebSocket失败:', error);
            connectionState = 'disconnected';
            reject(error);
            attemptReconnect();
        }
    });
}

/**
 * 尝试重新连接
 */
function attemptReconnect() {
    // 清除之前的重连定时器
    if (retryTimeout) {
        clearTimeout(retryTimeout);
        retryTimeout = null;
    }

    // 如果超过最大重试次数，则停止重试
    if (retryCount >= MAX_RETRY_COUNT) {
        console.log('已达到最大重试次数，停止重试');
        // showNotification('无法连接到游戏服务器，请稍后再试', 'error');
        return;
    }

    // 增加重试计数
    retryCount++;

    // 显示重试消息
    console.log(`WebSocket连接失败，${RETRY_DELAY/1000}秒后重试...（${retryCount}/${MAX_RETRY_COUNT}）`);
    // showNotification(`连接服务器失败，正在重试...（${retryCount}/${MAX_RETRY_COUNT}）`, 'warning');

    // 设置重连定时器
    retryTimeout = setTimeout(() => {
        initWebSocketConnection()
            .catch(error => {
                console.error('重新连接失败:', error);
            });
    }, RETRY_DELAY);
}

/**
 * 关闭WebSocket连接
 */
export function closeWebSocketConnection() {
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        connectionState = 'disconnected';
        ws.close();
        console.log('WebSocket连接已主动关闭');
    }
}

/**
 * 处理WebSocket消息
 * @param {MessageEvent} event WebSocket消息事件
 */
function handleWebSocketMessage(event) {
    try {
        const message = JSON.parse(event.data);

        // 特殊处理角色位置相关的消息
        if (message.type === 'character_position' || message.type === 'character_moved') {
            // 确保消息处理器存在
            if (messageHandlers.has(message.type)) {
                // 立即处理位置更新消息
                requestAnimationFrame(() => {
                    messageHandlers.get(message.type)(message.data);
                });
            }
            return;
        }

        // 其他消息的常规处理
        if (messageHandlers.has(message.type)) {
            messageHandlers.get(message.type)(message.data);
        } else {
            console.log(`未找到类型为 ${message.type} 的消息处理器`);
        }
    } catch (error) {
        console.error('处理WebSocket消息失败:', error);
    }
}

/**
 * 注册消息处理器
 * @param {string} messageType 消息类型
 * @param {Function} handler 处理函数
 */
export function registerMessageHandler(messageType, handler) {
    if (typeof handler !== 'function') {
        console.error('消息处理器必须是函数');
        return;
    }

    messageHandlers.set(messageType, handler);
    // console.log(`已注册 ${messageType} 消息处理器`);
}

/**
 * 移除消息处理器
 * @param {string} messageType 消息类型
 */
export function removeMessageHandler(messageType) {
    if (messageHandlers.has(messageType)) {
        messageHandlers.delete(messageType);
        // console.log(`已移除 ${messageType} 消息处理器`);
    }
}

/**
 * 发送WebSocket消息
 * @param {Object} message 消息对象
 * @returns {Promise<boolean>} 发送结果的Promise
 */
export function sendMessage(message) {
    return new Promise((resolve, reject) => {
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            console.log('WebSocket未连接，等待连接后发送消息');

            // 尝试重新连接
            if (connectionState === 'disconnected') {
                initWebSocketConnection()
                    .then(() => {
                        // 连接成功后发送消息
                        try {
                            ws.send(JSON.stringify(message));
                            resolve(true);
                        } catch (error) {
                            console.error('发送WebSocket消息失败:', error);
                            reject(error);
                        }
                    })
                    .catch(error => {
                        console.error('WebSocket连接失败:', error);
                        reject(error);
                    });
            } else {
                reject(new Error('WebSocket未连接且无法重连'));
            }
            return;
        }

        try {
            ws.send(JSON.stringify(message));
            resolve(true);
        } catch (error) {
            console.error('发送WebSocket消息失败:', error);
            reject(error);
        }
    });
}

/**
 * 获取WebSocket连接状态
 * @returns {string} 连接状态
 */
export function getConnectionState() {
    return connectionState;
}

/**
 * 防抖函数 - 避免短时间内重复发送相同请求
 * @param {string} key 请求的唯一标识
 * @param {Function} fn 要执行的函数
 * @param {Array} args 函数参数
 * @param {boolean} skipDebounce 是否跳过防抖
 * @returns {boolean} 是否执行了函数
 */
function debounce(key, fn, args, skipDebounce = false) {
    // 如果设置了跳过防抖，直接执行函数
    if (skipDebounce) {
        return fn(...args);
    }

    const now = Date.now();
    const lastRequest = recentRequests.get(key);

    // 如果最近没有相同请求或者已经超过防抖时间，则执行
    if (!lastRequest || now - lastRequest > DEBOUNCE_DELAY) {
        recentRequests.set(key, now);
        return fn(...args);
    } else {
        console.log(`请求 ${key} 被防抖机制拦截，${DEBOUNCE_DELAY}ms内不重复发送`);
        return false;
    }
}


// 获取所有在线角色的ID
export function getOnlineCharacter() {
    const requestKey = 'get_online_character';

    return debounce(requestKey, () => {
        return sendMessage({
            type: 'command',
            command: 'get_online_character'
        });
    }, [], false);
}

// 获取游戏状态
export function getGameStatus() {
    const requestKey = 'get_game_status';

    return debounce(requestKey, () => {
        return sendMessage({
            type: 'command',
            command: 'get_status'
        });
    }, [], false);
}

/**
 * 移动角色
 * @param {string} characterId 角色ID
 * @param {number} targetX 目标X坐标
 * @param {number} targetY 目标Y坐标
 * @returns {Promise} 发送结果的Promise
 */
export function moveCharacter(characterId, targetX, targetY) {
    // 使用 characterMovement.js 中的移动逻辑
    moveCharacterToPosition(characterId, { x: targetX, y: targetY });

    // 同时发送移动请求到服务器
    return sendMessage({
        type: 'command',
        command: 'move_character',
        character_id: characterId,
        position: {
            x: targetX,
            y: targetY
        }
    }).catch(error => {
        console.error('移动角色失败:', error);
        throw error;
    });
}


/**
 * 获取多个角色信息
 * @param {string[]} characterIds 角色ID数组
 * @returns {boolean} 是否发送成功
 */
export function getCharactersInfo(characterIds) {
    if (!characterIds || !Array.isArray(characterIds) || characterIds.length === 0) {
        // console.error('无效的角色ID数组');
        return false;
    }

    // 对角色ID数组排序，确保相同内容的数组生成相同的key
    const sortedIds = [...characterIds].sort();
    const requestKey = `get_characters_${sortedIds.join('_')}`;

    return debounce(requestKey, () => {
        return sendMessage({
            type: 'command',
            command: 'get_characters',
            character_ids: characterIds
        });
    }, []);
}

// 移除自动初始化WebSocket连接的代码

// 在页面卸载时关闭WebSocket连接
window.addEventListener('beforeunload', () => {
    closeWebSocketConnection();
});
